import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@shopify/polaris-icons';
import { Ic } from '../ui';

// Closes the popover when the user clicks anywhere else.
function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return { open, setOpen, ref };
}

function Trigger({ id, label, open, onClick }) {
  return (
    <button type="button" id={id} className="uh-ir-ms-btn" aria-expanded={open} aria-haspopup="listbox" onClick={onClick}>
      <span>{label}</span><Ic source={ChevronDownIcon} sm />
    </button>
  );
}

// value = selected ids; empty means "all".
export function MultiSelect({ id, options, value, onChange, allLabel, noun }) {
  const { open, setOpen, ref } = usePopover();
  const [q, setQ] = useState('');
  const shown = useMemo(() => options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase())), [options, q]);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const label = value.length ? `${value.length} of ${options.length} selected` : allLabel;
  return (
    <div className="uh-ir-ms" ref={ref}>
      <Trigger id={id} label={label} open={open} onClick={() => setOpen(!open)} />
      {open && (
        <div className="uh-ir-pop" role="listbox" aria-multiselectable="true">
          <div className="top">
            <input className="uh-inp" placeholder={`Find ${noun}`} aria-label={`Find ${noun}`} value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
            <button type="button" className="uh-link" onClick={() => onChange(options.map((o) => o.value))}>All</button>
            <button type="button" className="uh-link" onClick={() => onChange([])}>Clear</button>
          </div>
          {shown.map((o) => (
            <label key={o.value} className="uh-ir-opt">
              <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} />{o.label}
            </label>
          ))}
          {!shown.length && <div className="uh-ir-opt uh-muted">No matches</div>}
        </div>
      )}
    </div>
  );
}

// categories: [{id, parent_id, name}], already in Lightspeed's tree order.
export function CategorySelect({ id, categories, value, onChange }) {
  const { open, setOpen, ref } = usePopover();
  const [expanded, setExpanded] = useState(() => new Set());
  const children = useMemo(() => {
    const map = {};
    categories.forEach((c) => { (map[c.parent_id] = map[c.parent_id] || []).push(c); });
    return map;
  }, [categories]);
  const toggle = (cid) => onChange(value.includes(cid) ? value.filter((x) => x !== cid) : [...value, cid]);
  const flip = (cid) => setExpanded((prev) => { const n = new Set(prev); if (n.has(cid)) n.delete(cid); else n.add(cid); return n; });

  const node = (c, depth) => {
    const kids = children[c.id] || [];
    const isOpen = expanded.has(c.id);
    return (
      <React.Fragment key={c.id}>
        <div className="uh-ir-tree" style={{ paddingLeft: 8 + depth * 18 }}>
          <button type="button" className="tg" aria-label={isOpen ? `Collapse ${c.name}` : `Expand ${c.name}`}
            aria-expanded={kids.length ? isOpen : undefined} disabled={!kids.length} onClick={() => flip(c.id)}>
            {kids.length ? <Ic source={isOpen ? ChevronDownIcon : ChevronRightIcon} sm /> : null}
          </button>
          <label><input type="checkbox" checked={value.includes(c.id)} onChange={() => toggle(c.id)} />{c.name}</label>
          {kids.length > 0 && !isOpen && <span className="cc">{kids.length}</span>}
        </div>
        {isOpen && kids.map((k) => node(k, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="uh-ir-ms" ref={ref}>
      <Trigger id={id} label={value.length ? `${value.length} selected` : 'All categories'} open={open} onClick={() => setOpen(!open)} />
      {open && (
        <div className="uh-ir-pop">
          <div className="top">
            <button type="button" className="uh-link" onClick={() => onChange([])}>All categories</button>
            <button type="button" className="uh-link" onClick={() => setExpanded(new Set(categories.map((c) => c.id)))}>Expand all</button>
            <button type="button" className="uh-link" onClick={() => setExpanded(new Set())}>Collapse all</button>
          </div>
          {(children['0'] || []).map((c) => node(c, 0))}
        </div>
      )}
    </div>
  );
}
